import { type FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSnapCarousel } from 'react-snap-carousel';
import { formatArticleDate, getWindow, trackEvent } from '@surfline/web-common';
import { Box, IconButton, Button, Skeleton, Typography } from '@mui/material';
import CloudflareResizedImage from 'components/CloudflareResizedImage';
import type { CloudFlareLoaderAdditionalProps } from 'components/CloudflareResizedImage/CloudflareResizedImage';
import { useRouter } from 'next/router';
import { formatDistanceToNow } from 'date-fns';
import classNames from 'classnames';

import { ChevronLeft } from 'components/Icons/ChevronLeft';
import { ChevronRight } from 'components/Icons/ChevronRight';
import WavetrakLink from 'components/WavetrakLink';
import {
  useMaxWidthTablet,
  useMinWidthTablet,
  useMinWidthDesktop,
  useMinWidthDesktopLarge,
  useMinWidthMobileLarge,
  useMinWidthTabletLarge,
} from 'hooks/useMediaQueries';
import type { Article } from 'types/subregion';
import { convertToMilliseconds, oneSecond } from 'utils/time';
import { CarouselMode } from 'types/forecastArticles';

// @ts-expect-error Image can't be found on this path
import articleNoImage from '../../../public/images/article-no-image.png';
// @ts-expect-error Image can't be found on this path
import allStoriesTileMobile from '../../../public/images/all-stories-tile-mobile.png';
// @ts-expect-error Image can't be found on this path
import allStoriesTileTablet from '../../../public/images/all-stories-tile-tablet.png';
// @ts-expect-error Image can't be found on this path
import allStoriesTileDesktop from '../../../public/images/all-stories-tile-desktop.png';

import styles from './ForecastArticlesCarousel.module.scss';

// Backup function in case formatArticleDate doesn't format as expected
const formatRelativeTime = (timestamp: number): string => {
  try {
    // Convert seconds to milliseconds if needed
    const timeInMs = timestamp > 9999999999 ? timestamp : convertToMilliseconds(timestamp);
    return formatDistanceToNow(new Date(timeInMs), { addSuffix: true });
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return formatArticleDate(timestamp);
  }
};

const formatDateToYYYYMMDD = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toISOString().split('T')[0];
};

interface ForecastArticlesCarouselProps {
  articles: Article[];
  emptyText?: string;
  error?: Error;
  isActive?: boolean;
  isLoading?: boolean;
  mode?: CarouselMode;
  onRetry?: () => void;
  pageName?: string;
  subregionId?: string;
  subregionName?: string;
  title?: string;
  viewAllLink?: string;
}

interface ArticleCardProps {
  article: Article;
  index: number;
  isActive: boolean;
  snapPointIndexes: Set<number>;
  mode: CarouselMode;
  subregionName?: string;
  subregionId?: string;
}

interface ViewAllButtonProps {
  href?: string;
  mode: CarouselMode;
  pageName?: string;
  subregionName?: string;
  subregionId?: string;
  variant?: 'inline' | 'mobile' | 'default';
  className?: string;
}

const ArticleCard: FunctionComponent<ArticleCardProps> = ({
  article,
  index,
  isActive,
  snapPointIndexes,
  mode,
  subregionName,
  subregionId,
}) => {
  const { asPath, query } = useRouter();
  const isNative = query?.native?.toString() === 'true';
  const win = getWindow();

  const cloudflareOptions = useMemo<CloudFlareLoaderAdditionalProps>(
    () =>
      ({
        fit: 'cover',
        aspectRatio: 16 / 9,
      }) as CloudFlareLoaderAdditionalProps,
    [],
  );

  const handleTrackEvent = useCallback(() => {
    trackEvent('Loaded a Page', {
      url: article.permalink,
      name: 'editorial',
      path: article.permalink,
      title: article.content.title,
      postId: article.id,
      search: win?.location?.search ?? null,
      channel: 'editorial',
      postDate: formatDateToYYYYMMDD(article.createdAt),
      postTags: article.tags.map((tag) => tag.name),
      referrer: asPath,
      authorName: article.author.name,
      postSeries: article.series?.map((series) => series.name),
      postCategories: article.categories?.map((category) => category.name),
      isPremiumArticle: article.premium,
      isSponsoredArticle: !!article.sponsoredArticle?.sponsorName,
      postCurrentPromotion: article.promoted?.[0] ?? null,
      subregionName,
      subregionId,
    });
  }, [article, win?.location?.search, asPath, subregionName, subregionId]);

  const imageUrl = useMemo(
    () => (article.media?.feed1x ? article.media?.feed1x : articleNoImage),
    [article.media?.feed1x],
  );

  const relativeTime = useMemo(
    () => formatRelativeTime(article.createdAt * oneSecond),
    [article.createdAt],
  );

  return (
    <li
      className={classNames(styles.item, {
        [styles.scrollSnapAlign]: snapPointIndexes?.has(index),
        [styles.active]: isActive,
      })}
      data-testid="forecast-article-card"
      key={article.id}
    >
      <WavetrakLink
        className={classNames(styles.card, {
          [styles.cardDark]: mode === 'dark',
          [styles.cardLight]: mode === 'light',
        })}
        data-testid="forecast-article-link"
        href={isNative ? `${article.permalink}?native=true` : article.permalink}
        isExternal={isNative}
        onClick={handleTrackEvent}
      >
        <div className={styles.cardImage}>
          <CloudflareResizedImage
            src={imageUrl}
            alt={article.content.title}
            className={styles.cardImageInner}
            loading="lazy"
            objectFit="cover"
            objectPosition="center"
            layout="fill"
            sizes="(max-width: 768px) 340px,
             ((min-width: 769px) and (max-width: 1319px)) 388px,
             (min-width: 1320px) 521px"
            quality={100}
            cloudflareOptions={cloudflareOptions}
          />
          {article.premium && (
            <div className={styles.premiumBadge} data-testid="premium-badge">
              Premium
            </div>
          )}
        </div>
        <div className={styles.cardInfo}>
          <Typography
            variant="subHeadline"
            component="h3"
            className={classNames(styles.cardTitle, {
              [styles.cardTitleDark]: mode === 'dark',
              [styles.cardTitleLight]: mode === 'light',
            })}
          >
            {article.content.title}
          </Typography>
          <Typography
            variant="caption"
            component="p"
            className={classNames(styles.cardTime, {
              [styles.cardTimeDark]: mode === 'dark',
              [styles.cardTimeLight]: mode === 'light',
            })}
          >
            {relativeTime}
          </Typography>
        </div>
      </WavetrakLink>
    </li>
  );
};

const LoadingSkeleton: FunctionComponent<{ mode: CarouselMode }> = ({ mode }) => {
  const isMinWidthTablet = useMinWidthTablet();
  const isMinWidthDesktop = useMinWidthDesktop();
  const isMinWidthDesktopLarge = useMinWidthDesktopLarge();

  const itemCount = useMemo(() => {
    if (isMinWidthDesktopLarge) return 4;
    if (isMinWidthDesktop) return 3;
    if (isMinWidthTablet) return 2;
    return 1;
  }, [isMinWidthDesktop, isMinWidthDesktopLarge, isMinWidthTablet]);

  const loadingItems = useMemo(
    () =>
      Array.from({ length: itemCount }).map((_, i) => (
        <Box key={`skeleton-item-${i.toString()}`} className={styles.item}>
          <Box
            className={classNames(styles.card, {
              [styles.cardDark]: mode === 'dark',
              [styles.cardLight]: mode === 'light',
            })}
          >
            <Skeleton variant="rectangular" className={styles.loadingSkeleton} animation="wave" />
            <Box className={styles.cardInfo}>
              <Skeleton className={styles.loadingTitle} animation="wave" />
              <Skeleton className={styles.loadingSubtitle} animation="wave" />
            </Box>
          </Box>
        </Box>
      )),
    [itemCount, mode],
  );

  return (
    <div className="sl-section-full-bleed">
      <Box className={styles.scrollContainer}>{loadingItems}</Box>
    </div>
  );
};

const ViewAllButton: FunctionComponent<ViewAllButtonProps> = ({
  href,
  mode,
  pageName = 'Home',
  subregionName,
  subregionId,
  variant = 'default',
  className,
}) => {
  const { query } = useRouter();
  const isNative = query?.native?.toString() === 'true';

  const formattedViewAllLink = useMemo(() => {
    if (!href) return undefined;
    return isNative ? 'surfline://feed' : href;
  }, [href, isNative]);

  const handleClick = useCallback(() => {
    const segmentProperties = {
      linkText: 'View all Stories',
      location: 'Content Carousel',
      pageName,
      subregionName,
      subregionId,
    };
    trackEvent('Clicked Link', segmentProperties);
  }, [pageName, subregionName, subregionId]);

  if (!formattedViewAllLink) return null;

  const buttonClasses = classNames(
    {
      [styles.viewAllButton]: variant === 'inline',
      [styles.viewAllButtonDark]: variant === 'inline' && mode === 'dark',
      [styles.viewAllButtonLight]: variant === 'inline' && mode === 'light',
      [styles.viewAllStoriesButton]: variant !== 'inline',
      [styles.viewAllStoriesButtonDark]: variant !== 'inline' && mode === 'dark',
      [styles.viewAllStoriesButtonLight]: variant !== 'inline' && mode === 'light',
    },
    className,
  );

  return (
    <Button
      variant={variant === 'inline' ? 'secondary' : undefined}
      className={buttonClasses}
      href={formattedViewAllLink}
      onClick={handleClick}
      data-testid={`forecast-articles-view-all${variant !== 'default' ? `-${variant}` : ''}`}
      disableRipple
      disableFocusRipple
      disableTouchRipple
    >
      View all Stories
    </Button>
  );
};

const EmptyState: FunctionComponent<{
  viewAllLink?: string;
  mode: CarouselMode;
  emptyText?: string;
}> = ({ viewAllLink, mode, emptyText = 'No recent stories for this region.' }) => {
  const isMinWidthMobileLarge = useMinWidthMobileLarge();
  const isMinWidthTabletLarge = useMinWidthTabletLarge();

  const emptyStateImageSrc = useMemo(() => {
    if (isMinWidthTabletLarge) return allStoriesTileDesktop;
    if (isMinWidthMobileLarge) return allStoriesTileTablet;
    return allStoriesTileMobile;
  }, [isMinWidthTabletLarge, isMinWidthMobileLarge]);

  return (
    <Box data-testid="forecast-articles-empty">
      <Box className={styles.emptyState}>
        <Typography
          variant="footnote"
          component="p"
          className={classNames(styles.emptyText, {
            [styles.emptyTextDark]: mode === 'dark',
            [styles.emptyTextLight]: mode === 'light',
          })}
        >
          {emptyText}
        </Typography>
        <div className={styles.emptyImageWrapper}>
          <div className={styles.emptyImageContainer}>
            <CloudflareResizedImage
              src={emptyStateImageSrc}
              alt="No recent stories for this region."
              className={styles.emptyImage}
              layout="fill"
              objectFit="cover"
              sizes="(max-width: 511px) 398px,
                     ((min-width: 512px) and (max-width: 975px)) 760px,
                     (min-width: 976px) 1600px"
            />
          </div>
        </div>
        <ViewAllButton href={viewAllLink} mode={mode} />
      </Box>
    </Box>
  );
};

const ErrorState: FunctionComponent<{
  onRetry?: () => void;
  viewAllLink?: string;
  mode: CarouselMode;
}> = ({ onRetry, viewAllLink, mode }) => (
  <Box data-testid="forecast-articles-error">
    <Box
      className={classNames(styles.errorState, {
        [styles.errorStateLight]: mode === 'light',
      })}
    >
      <Typography
        variant="callout1"
        component="h3"
        className={classNames({
          [styles.errorTitleDark]: mode === 'dark',
          [styles.errorTitleLight]: mode === 'light',
        })}
      >
        Oops, we&apos;re sorry.
      </Typography>
      <Typography
        variant="footnote"
        component="p"
        className={classNames(styles.errorText, {
          [styles.errorTextDark]: mode === 'dark',
          [styles.errorTextLight]: mode === 'light',
        })}
      >
        Something went wrong here.
      </Typography>
      <Button
        className={styles.retryButton}
        onClick={onRetry}
        data-testid="forecast-articles-retry"
        disableRipple
        disableFocusRipple
        disableTouchRipple
      >
        Try again
      </Button>
    </Box>
    <ViewAllButton href={viewAllLink} mode={mode} />
  </Box>
);

const ForecastArticlesCarousel: FunctionComponent<ForecastArticlesCarouselProps> = ({
  articles = [],
  emptyText,
  error,
  isActive = true,
  isLoading = false,
  mode = 'light',
  onRetry,
  pageName = 'Home',
  subregionId,
  subregionName,
  title,
  viewAllLink,
}) => {
  const isMaxWidthTablet = useMaxWidthTablet();
  const {
    activePageIndex,
    hasNextPage,
    hasPrevPage,
    next,
    pages,
    prev,
    refresh,
    scrollRef,
    snapPointIndexes,
  } = useSnapCarousel(articles.length ? { initialPages: [[0]] } : undefined);

  const isMinWidthTablet = useMinWidthTablet();
  const isMinWidthDesktop = useMinWidthDesktop();
  const isMinWidthDesktopLarge = useMinWidthDesktopLarge();

  const onClickPrevious = useCallback(() => {
    prev();
  }, [prev]);

  const onClickNext = useCallback(() => {
    next();
  }, [next]);

  const slidesPerPage = useMemo(() => {
    if (isMinWidthDesktopLarge) return 4;
    if (isMinWidthDesktop) return 3;
    if (isMinWidthTablet) return 2;
    return 1;
  }, [isMinWidthDesktop, isMinWidthDesktopLarge, isMinWidthTablet]);

  const slides = useMemo(
    () =>
      articles.map((article, index) => {
        const totalPages = pages?.length || 0;
        const isLastPage = activePageIndex === totalPages - 1;

        // For the last page with insufficient items, create a sliding window
        const startIndex =
          isLastPage && articles.length % slidesPerPage !== 0
            ? Math.max(articles.length - slidesPerPage, 0) // Show last slidesPerPage items
            : activePageIndex * slidesPerPage; // Normal pagination

        const endIndex = Math.min(startIndex + slidesPerPage, articles.length);
        const isCardActive = isActive && index >= startIndex && index < endIndex;

        return (
          <ArticleCard
            article={article}
            index={index}
            isActive={isCardActive}
            key={article.id}
            snapPointIndexes={snapPointIndexes}
            mode={mode}
            subregionName={subregionName}
            subregionId={subregionId}
          />
        );
      }),
    [
      activePageIndex,
      articles,
      isActive,
      mode,
      pages?.length,
      slidesPerPage,
      snapPointIndexes,
      subregionId,
      subregionName,
    ],
  );

  const formattedTitle = useMemo(() => (title ? `${title} Stories` : 'Stories'), [title]);

  const prevSlidesLength = useRef(slides.length);

  useEffect(() => {
    if (slides.length !== prevSlidesLength.current) {
      refresh();
      prevSlidesLength.current = slides.length;
    }
  }, [slides, refresh]);

  if (isLoading) {
    return (
      <Box
        className={classNames({
          [styles.containerDark]: mode === 'dark',
          [styles.containerLight]: mode === 'light',
        })}
        component="section"
        data-testid="forecast-articles-loading"
      >
        <div
          className={classNames(styles.header, {
            [styles.headerDark]: mode === 'dark',
            [styles.headerLight]: mode === 'light',
          })}
        >
          <Typography
            variant="callout1"
            component="h2"
            className={classNames(styles.title, {
              [styles.titleDark]: mode === 'dark',
              [styles.titleLight]: mode === 'light',
            })}
          >
            {formattedTitle}
          </Typography>
        </div>
        <LoadingSkeleton mode={mode} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        className={classNames({
          [styles.containerDark]: mode === 'dark',
          [styles.containerLight]: mode === 'light',
        })}
        component="section"
        data-testid="forecast-articles-error-container"
      >
        <div
          className={classNames(styles.header, {
            [styles.headerDark]: mode === 'dark',
            [styles.headerLight]: mode === 'light',
          })}
        >
          <Typography
            variant="callout1"
            component="h2"
            className={classNames(styles.title, {
              [styles.titleDark]: mode === 'dark',
              [styles.titleLight]: mode === 'light',
            })}
          >
            {formattedTitle}
          </Typography>
        </div>
        <ErrorState onRetry={onRetry} viewAllLink={viewAllLink} mode={mode} />
      </Box>
    );
  }

  if (!articles.length) {
    return (
      <Box
        className={classNames({
          [styles.containerDark]: mode === 'dark',
          [styles.containerLight]: mode === 'light',
        })}
        component="section"
        data-testid="forecast-articles-empty-container"
      >
        <div
          className={classNames(styles.header, {
            [styles.headerDark]: mode === 'dark',
            [styles.headerLight]: mode === 'light',
          })}
        >
          <Typography
            variant="callout1"
            component="h2"
            className={classNames(styles.title, {
              [styles.titleDark]: mode === 'dark',
              [styles.titleLight]: mode === 'light',
            })}
          >
            {formattedTitle}
          </Typography>
        </div>
        <EmptyState emptyText={emptyText} mode={mode} viewAllLink={viewAllLink} />
      </Box>
    );
  }

  return (
    <Box
      className={classNames({
        [styles.containerDark]: mode === 'dark',
        [styles.containerLight]: mode === 'light',
      })}
      component="section"
      data-testid="forecast-articles-carousel"
    >
      <div
        className={classNames(styles.header, {
          [styles.headerDark]: mode === 'dark',
          [styles.headerLight]: mode === 'light',
        })}
      >
        <Typography
          variant="callout1"
          component="h2"
          className={classNames(styles.title, {
            [styles.titleDark]: mode === 'dark',
            [styles.titleLight]: mode === 'light',
          })}
        >
          {formattedTitle}
        </Typography>
        <div className={styles.actions}>
          <ViewAllButton
            href={viewAllLink}
            mode={mode}
            variant="inline"
            pageName={pageName}
            subregionName={subregionName}
            subregionId={subregionId}
          />
          <IconButton
            className={styles.arrow}
            data-testid="forecast-articles-previous"
            disabled={!hasPrevPage}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            onClick={onClickPrevious}
            size="small"
            aria-label="Previous Forecast Articles"
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            className={styles.arrow}
            data-testid="forecast-articles-next"
            disabled={!hasNextPage}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            onClick={onClickNext}
            size="small"
            aria-label="Next Forecast Articles"
          >
            <ChevronRight />
          </IconButton>
        </div>
      </div>
      <div className="sl-section-full-bleed">
        <ul className={styles.scrollContainer} ref={scrollRef}>
          {slides}
        </ul>
      </div>
      {isMaxWidthTablet && (
        <ViewAllButton
          href={viewAllLink}
          mode={mode}
          variant="mobile"
          pageName={pageName}
          subregionName={subregionName}
          subregionId={subregionId}
        />
      )}
    </Box>
  );
};

export default ForecastArticlesCarousel;
